import { isPlatformServer, LocationStrategy, registerLocaleData } from '@angular/common';
import { PdAliasLocationStrategy } from './pd-alias-location-strategy';
import { setAppInjector } from './app-injector';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localePl from '@angular/common/locales/pl';
import { CUSTOM_ELEMENTS_SCHEMA, Inject, Injector, makeStateKey, NgModule, PLATFORM_ID, TransferState } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { SETTINGS as FIRESTORE_SETTINGS } from '@angular/fire/compat/firestore';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { print } from 'graphql';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { Apollo, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { CookieService } from 'ngx-cookie-service';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { environment } from 'src/environments/environment';
import { SwiperModule } from 'swiper/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthGuard } from './auth.guard';
import {
  LoginDialogComponent,
  LoginDialogRouteComponent,
  LoginStandaloneComponent
} from './components/auth/login-dialog/login-dialog.component';
import { LoginComponent } from './components/auth/login/login.component';
import { WrongPasswordDialogComponent } from './components/auth/login/wrong-password/wrong-password-dialog/wrong-password-dialog.component';
import { WrongPasswordComponent } from './components/auth/login/wrong-password/wrong-password.component';
import { LogoutComponent } from './components/auth/logout/logout.component';
import {
  LostPasswordDialogComponent,
  LostPasswordDialogRouteComponent
} from './components/auth/lost-password-dialog/lost-password-dialog.component';
import { LostPasswordComponent } from './components/auth/lost-password/lost-password.component';
import { NewPasswordComponent } from './components/auth/new-password/new-password.component';
import { StepperComponent } from './components/auth/signup/stepper/stepper.component';
import { BasePageComponent } from './components/base-page/base-page.component';
import { BaseComponent } from './components/base/base.component';
import { CashbackHistoryComponent } from './components/client-area/main-client-area/base/cashback-history/cashback-history.component';
import { BreadcrumbsComponent } from './components/desktop/breadcrumbs/breadcrumbs.component';
import { DesktopFrontPageComponent } from './components/desktop/desktop-front-page/desktop-front-page.component';
import { DesktopSidenavComponent } from './components/desktop/desktop-sidenav/desktop-sidenav.component';
import { DesktopTopMenuComponent } from './components/desktop/desktop-top-menu/desktop-top-menu.component';
import { DESKTOP_MODULE_ROUTES } from './components/desktop/desktop.routing.module';
import { DesktopFooterComponent } from './components/desktop/footer/footer.component';
import { BonusExcludedGameComponent } from './components/desktop/game-launcher/bonus_excluded-dialog/bonus_excluded-dialog.component';
import { ConfirmDialogComponent } from './components/dialog/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from './components/dialog/error-dialog/error-dialog.component';
import { GameDialogComponent, GameDialogRouteComponent } from './components/dialog/game-dialog/game-dialog.component';
import {
  ResumeGameDialogComponent,
  ResumeGameDialogRouteComponent
} from './components/dialog/resume-game-dialog/resume-game-dialog.component';
import { SuccessDialogComponent } from './components/dialog/success-dialog/success-dialog.component';
import { VipUpgradeDialogComponent } from './components/dialog/vip-upgrade-dialog/vip-upgrade-dialog.component';
import { FooterComponent } from './components/footer/footer.component';
import { CategoryScrollComponent } from './components/front-page/category-scroll/category-scroll.component';
import { DesktopCategoryMenuComponent } from './components/front-page/desktop-category-menu/desktop-category-menu.component';
import { FrontPageComponent } from './components/front-page/front-page.component';
import { GameCategoryDialogComponent } from './components/front-page/game-category-dialog/game-category-dialog.component';
import { GamificationProgressComponent } from './components/front-page/gamification-progress/gamification-progress.component';
import { LastPlayedComponent } from './components/front-page/last-played/last-played.component';
import { NotificationsEmailComponent } from './components/front-page/notifications-email/notifications-email.component';
import { AcceptTermsNoticeComponent } from './components/accept-terms-notice/accept-terms-notice.component';
import { BaseGamePage } from './components/games/ase-game-page.component';
import { LastPlayedFullComponent } from './components/games/last-played-full/last-played-full.component';
import { MyGamesComponent } from './components/games/my-games/my-games.component';
import { AppSwiperModule } from './components/games/search-game/swiper-module/swiper.module';

import { NotFoundComponent } from './components/not-found/not-found.component';
import { NotificationSheetComponent } from './components/notifications/notification-sheet/notification-sheet.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { TermsDialogComponent } from './components/payment/deposit/deposit-bonus-selector/terms-dialog/terms-dialog.component';
import { DesktopTopProfileInfoComponent } from './components/profile/desktop-top-profile-info/desktop-top-profile-info.component';
import { ResponsibleGamingComponent } from './components/profile/responsible-gaming/responsible-gaming.component';
import { StaticPageComponent } from './components/static-page/static-page.component';
import { BottomToolbarComponent } from './components/toolbar/bottom-toolbar/bottom-toolbar.component';
import { ProfileButtonComponent } from './components/toolbar/profile-button/profile-button.component';
import { TopMenuBalanceComponent } from './components/top-menu/top-menu-balance/top-menu-balance.component';
import { TopMenuButtonComponent } from './components/top-menu/top-menu-button/top-menu-button.component';
import { TopMenuItemComponent } from './components/top-menu/top-menu-item/top-menu-item.component';
import { TopMenuComponent } from './components/top-menu/top-menu.component';
import { LoaderComponent } from './components/utils/loader/loader.component';
import { DownloadInfobarComponent } from './components/utils/pwa/download-infobar/download-infobar.component';
import { InstallPopupComponent } from './components/utils/pwa/install-popup/install-popup.component';
import { AppShellNoRenderDirective } from './directives/ssr-no-render.directive';
import { DrawerGuard } from './drawer.guard';
import { CACHE_HOST_OVERRIDE, DESKTOP_ROUTES, HOST_NAME } from './global.tokens';
import { MobileOperatorsModule } from './modules/mobile-operators/mobile-operators.module';
import { SharedComponentsModule } from './modules/shared-components/shared-components.module';
import { SharedMaterialModule } from './modules/shared-material/shared-material.module';
import { SharedServicesModule } from './modules/shared-services/shared-services.module';
import { TranslateUrlPipe } from './pipes/translate-url.pipe';
import { MinimizePageResolver } from './resolvers/minimize-page.resolver';
import { MaximumPageResolver } from './resolvers/restore-max-page.resolver';
import { CheckSessionService } from './services/auth/check-session/check-session.service';
import { UserInfoService } from './services/auth/check-session/user-info.service';
import { FirebaseAuthService } from './services/auth/firebase/firebase-auth.service';
import { CACHED_CMS_QUERY } from "./services/http/base-http.service";
import { NavigationService } from './services/navigation/navigation.service';
import { BonusListService } from './services/payment/bonus/bonus-list.service';
import { BalanceStatusService } from './services/wallet/balance-status.service';
import { formatLocaleCMS } from './utils/format-locale-cms';
import { BirthDatePickerWidget } from './utils/birth-date-picker/birth-date-picker';
import { VipChatTriggerComponent } from './utils/vip-chat/vip-chat-trigger.component';
import { DraggableChatTriggerComponent } from './utils/vip-chat/draggable-chat-trigger.component';
import { JackpotCardModule } from './modules/jackpot/jackpot-card.module';
import { DomainRedirectDialogComponent } from './components/domain-redirect-dialog/domain-redirect-dialog.component';
import { CurrencyConversionDialogComponent } from './components/dialog/currency-conversion-dialog/currency-conversion-dialog.component';
import { ResponsibleGamingDialogComponent } from './components/dialog/responsible-gaming-dialog/responsible-gaming-dialog.component';
import { CrossSellBonusDialogComponent } from './components/cross-sell-bonus-dialog/cross-sell-bonus-dialog.component';

registerLocaleData(localePl, 'pl');

const STATE_KEY = makeStateKey<any>('apollo.state');
const TEST_STATE_KEY = makeStateKey<any>('test.state');
const FRONT_PAGE_REQUESTS_STATE_KEY = makeStateKey<any[]>('ssr.front-page-games.requests');

@NgModule({
  declarations: [
    AppComponent, FrontPageComponent, StaticPageComponent,
    BasePageComponent,
    BaseComponent,
    NotFoundComponent,
    AppShellNoRenderDirective,
    GameDialogComponent,
    ResumeGameDialogComponent,
    TopMenuComponent,
    FooterComponent,
    LoginComponent,
    LoginDialogComponent,
    LoginStandaloneComponent,
    LostPasswordComponent,
    LogoutComponent,
    WrongPasswordComponent,
    WrongPasswordDialogComponent,
    BottomToolbarComponent,
    ErrorDialogComponent,
    SuccessDialogComponent,
    VipUpgradeDialogComponent,
    ConfirmDialogComponent,
    ResponsibleGamingDialogComponent,
    BonusExcludedGameComponent,
    LoaderComponent,
    TopMenuButtonComponent,
    GameCategoryDialogComponent,
    TopMenuItemComponent,
    TopMenuBalanceComponent,
    NotificationsComponent,
    NotificationSheetComponent,
    LastPlayedComponent,
    LastPlayedFullComponent,
    MyGamesComponent,
    StepperComponent,
    TermsDialogComponent,
    DesktopCategoryMenuComponent,
    DesktopTopProfileInfoComponent,
    GameDialogRouteComponent,
    ResumeGameDialogRouteComponent,
    BaseGamePage,
    LoginDialogRouteComponent,
    TranslateUrlPipe,
    CategoryScrollComponent,
    NewPasswordComponent,
    ProfileButtonComponent,
    ResponsibleGamingComponent,
    LostPasswordDialogRouteComponent,
    LostPasswordDialogComponent,
    BreadcrumbsComponent,
    DesktopFrontPageComponent,
    DesktopSidenavComponent,
    DesktopTopMenuComponent,
    DesktopFooterComponent,
    DownloadInfobarComponent,
    InstallPopupComponent,
    GamificationProgressComponent,
    NotificationsEmailComponent,
    AcceptTermsNoticeComponent,
    CashbackHistoryComponent,
    DomainRedirectDialogComponent,
    CurrencyConversionDialogComponent,
    CrossSellBonusDialogComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    SharedServicesModule.forRoot(),
    TransferHttpCacheModule,
    AppRoutingModule,
    FlexLayoutModule,
    BrowserAnimationsModule,
    SharedMaterialModule,
    SwiperModule,
    FormsModule,
    ReactiveFormsModule,
    ApolloModule,
    SharedComponentsModule,
    InfiniteScrollModule,
    MobileOperatorsModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.enableServiceWorker || false }),
    AppSwiperModule,
    BirthDatePickerWidget,
    VipChatTriggerComponent,
    DraggableChatTriggerComponent,
    JackpotCardModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    CookieService,
    FirebaseAuthService,
    CheckSessionService,
    BalanceStatusService,
    UserInfoService,
    BonusListService,
    DrawerGuard,
    Title,
    MinimizePageResolver,
    MaximumPageResolver,
    AuthGuard,
    NavigationService,
    {
      provide: FIRESTORE_SETTINGS,
      useValue: { ignoreUndefinedProperties: true }
    },
    {
      provide: DESKTOP_ROUTES,
      useValue: DESKTOP_MODULE_ROUTES
    },
    {
      provide: LocationStrategy,
      useClass: PdAliasLocationStrategy
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],

  bootstrap: [AppComponent]
})
export class AppModule {
  static mInjector: Injector;
  cache: InMemoryCache;

  constructor(
    apollo: Apollo,
    httpLink: HttpLink,
    protected transferState: TransferState,
    injector: Injector,
    @Inject(PLATFORM_ID) private platformId,
    @Inject(HOST_NAME) public hostName,
    @Inject(CACHE_HOST_OVERRIDE) public cacheHostOverride
  ) {
    AppModule.mInjector = injector;
    setAppInjector(injector);
    this.cache = new InMemoryCache({
      typePolicies: {
        Game: {
          fields: {
            // Preserve fields on nested objects when queries write different
            // selections for the same normalized game during SSR hydration.
            gameProvider: { merge: true },
            thumbnail: { merge: true },
            thumbnailDesktopSquare: { merge: true },
            thumbnailDesktopRectangular: { merge: true },
          }
        },
      },
    });
    const isBrowser = isPlatformServer(this.platformId) === false;

    const basic = setContext((operation, context) => ({
      headers: !!environment.cmsDraftToken ? {
        Authorization: `Bearer ${environment.cmsDraftToken}`,

      } : {
        // "Accept": `application/json, text/plain, */*, ${new Date().getTime().toString()}`
      }
    }));


    const localeMiddleware = new ApolloLink((operation, forward) => {
      // add the authorization to the headers
      // we assume `headers` as a defined instance of HttpHeaders
      let v = operation.variables;
      if (operation.variables['locale']) {
        operation.variables['locale'] = formatLocaleCMS(operation.variables['locale']);
      }
      if (operation.variables['countryName']) {
        if (Array.isArray(operation.variables['countryName'])) {
          operation.variables['countryName'] = operation.variables['countryName'].map(country =>
            formatLocaleCMS(country)
          );
        } else {
          operation.variables['countryName'] = [formatLocaleCMS(operation.variables['countryName'])];
        }
      }
      operation.variables['buildversion'] = '3299900024';
      return forward(operation);
    });

    //It souldnt be here only to play and debug
    this.transferState.set(TEST_STATE_KEY, <string>'hello');

    const cacheHostOverrideValue = (this.cacheHostOverride || '').trim();
    const cachedCms = isBrowser
      ? (environment.production ? environment.cmsCacheUrl + this.hostName.replace('cache.', '') + CACHED_CMS_QUERY : environment.cmsApi)
      : (cacheHostOverrideValue
        ? `https://cms.${cacheHostOverrideValue}` + CACHED_CMS_QUERY
        : (process.env['PUBLIC_CACHE_HOST'] ? `https://cms.${process.env['PUBLIC_CACHE_HOST']}` + CACHED_CMS_QUERY : environment.cmsApi));

    const serverRequestLogger = new ApolloLink((operation, forward) => {
      const isFrontPageRequest = operation.operationName === 'FrontPageGames' || operation.operationName === 'FrontPageGamesDestkop';
      if (!isBrowser && isFrontPageRequest) {
        const context = operation.getContext();
        const requestBody = {
          operationName: operation.operationName || null,
          query: print(operation.query),
          variables: operation.variables || {},
          extensions: operation.extensions || null
        };
        const requestSnapshot = {
          host: this.hostName,
          cacheHostOverride: this.cacheHostOverride || null,
          uri: cachedCms,
          body: requestBody,
          params: {
            headers: context?.headers || null,
            credentials: context?.credentials || null,
            withCredentials: context?.withCredentials || null,
            fetchOptions: context?.fetchOptions || null,
            http: context?.http || null
          }
        };
        const existingRequests = this.transferState.get<any[]>(FRONT_PAGE_REQUESTS_STATE_KEY, []);
        this.transferState.set(FRONT_PAGE_REQUESTS_STATE_KEY, [...existingRequests, requestSnapshot]);
      }

      return forward(operation);
    });


    apollo.create({
      link: ApolloLink.from([basic, localeMiddleware, serverRequestLogger, httpLink.create({ uri: cachedCms })]),
      cache: this.cache,

      ...(isBrowser
        ? {
          // queries with `forceFetch` enabled will be delayed
          ssrForceFetchDelay: 200,
        }
        : {
          // avoid to run twice queries with `forceFetch` enabled
          ssrMode: true,
        }),
    });
    if (isBrowser) {
      this.onBrowser();
    } else {
      this.onServer();

    }


  }

  onServer() {
    // console.log("serializing", this.transferState, STATE_KEY)
    this.transferState.onSerialize(STATE_KEY, () => {
      return this.cache.extract();
    });
  }

  onBrowser() {
    const state = this.transferState.get<any>(STATE_KEY, null);
    this.cache.restore(state);
  }

}
